import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Download, Printer, Loader2, FileText, AlertCircle, 
  Maximize2, Minimize2, Share2, ChevronDown 
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const PDFPreviewModal = ({ 
  isOpen, 
  onClose, 
  pdfBlob, 
  title = 'Document Preview',
  filename = 'document.pdf',
  isLoading = false,
  error = null 
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return () => {};
  }, [pdfBlob]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleDownload = useCallback(() => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Document downloaded');
      if (isMobile) setShowMobileActions(false);
    }
  }, [pdfUrl, filename, isMobile]);

  const handlePrint = useCallback(() => {
    if (pdfUrl) {
      // Open PDF in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        // Try to trigger print after a short delay (browser-dependent)
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (e) {
            // Some browsers block print() from scripts - user can still use browser's print menu
          }
        }, 500);
      }
      if (isMobile) setShowMobileActions(false);
    }
  }, [pdfUrl, isMobile]);

  const handleShare = useCallback(async () => {
    if (pdfBlob && navigator.share) {
      try {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        await navigator.share({
          files: [file],
          title: title,
        });
        toast.success('Shared successfully');
      } catch (e) {
        if (e.name !== 'AbortError') {
          // Fallback to download if share fails
          handleDownload();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      handleDownload();
    }
    if (isMobile) setShowMobileActions(false);
  }, [pdfBlob, filename, title, handleDownload, isMobile]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="pdf-preview-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className={`
            relative bg-[var(--background-paper)] shadow-2xl border border-[var(--border-primary)] 
            flex flex-col overflow-hidden transition-all duration-300
            ${isFullscreen || isMobile 
              ? 'w-full h-full rounded-none' 
              : 'w-full max-w-5xl h-[90vh] rounded-xl mx-4'
            }
          `}
        >
          {/* Header - Touch Optimized */}
          <div className={`
            flex items-center justify-between border-b border-[var(--border-primary)] 
            bg-[var(--background-elevated)] shrink-0
            ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}
          `}>
            {/* Left: Document Info */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`
                rounded-lg bg-[var(--primary)]/10 shrink-0
                ${isMobile ? 'p-2' : 'p-2'}
              `}>
                <FileText className={`text-[var(--primary)] ${isMobile ? 'w-5 h-5' : 'w-5 h-5'}`} />
              </div>
              <div className="min-w-0">
                <h2 className={`font-semibold text-[var(--text-primary)] truncate ${isMobile ? 'text-base' : 'text-lg'}`}>
                  {title}
                </h2>
                <p className="text-xs text-[var(--text-secondary)] truncate">{filename}</p>
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Desktop Actions */}
              {!isMobile && pdfUrl && !isLoading && !error && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrint}
                    className="gap-2 border-[var(--border-primary)] hover:bg-[var(--primary)]/10"
                    data-testid="pdf-print-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleDownload}
                    className="gap-2 bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-black"
                    data-testid="pdf-download-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleFullscreen}
                    className="hover:bg-white/10"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                </>
              )}
              
              {/* Close Button - Large touch target on mobile */}
              <Button
                variant="ghost"
                size={isMobile ? 'default' : 'icon'}
                onClick={onClose}
                className={`
                  hover:bg-white/10 
                  ${isMobile ? 'min-w-[44px] min-h-[44px] p-2' : 'ml-2'}
                `}
                data-testid="pdf-close-btn"
              >
                <X className={isMobile ? 'w-6 h-6' : 'w-5 h-5'} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden bg-gray-900 relative">
            {isLoading && (
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <Loader2 className={`text-[var(--primary)] animate-spin ${isMobile ? 'w-16 h-16' : 'w-12 h-12'}`} />
                <p className={`text-[var(--text-secondary)] ${isMobile ? 'text-lg' : 'text-base'}`}>
                  Loading document...
                </p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <div className={`rounded-full bg-red-500/10 ${isMobile ? 'p-6' : 'p-4'}`}>
                  <AlertCircle className={`text-red-500 ${isMobile ? 'w-16 h-16' : 'w-12 h-12'}`} />
                </div>
                <div className="text-center">
                  <p className={`font-medium text-[var(--text-primary)] ${isMobile ? 'text-xl' : 'text-lg'}`}>
                    Failed to load document
                  </p>
                  <p className={`text-[var(--text-secondary)] mt-2 ${isMobile ? 'text-base' : 'text-sm'}`}>
                    {error}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={onClose} 
                  className={`mt-4 ${isMobile ? 'min-h-[48px] px-8 text-base' : ''}`}
                >
                  Close
                </Button>
              </div>
            )}

            {pdfUrl && !isLoading && !error && (
              <iframe
                src={pdfUrl}
                title={title}
                className="w-full h-full border-0"
                data-testid="pdf-iframe"
              />
            )}
          </div>

          {/* Mobile Bottom Action Bar - Touch Optimized */}
          {isMobile && pdfUrl && !isLoading && !error && (
            <div className="shrink-0 border-t border-[var(--border-primary)] bg-[var(--background-elevated)] safe-area-inset-bottom">
              {/* Collapsed Action Bar */}
              {!showMobileActions ? (
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    {/* Quick Download */}
                    <button
                      onClick={handleDownload}
                      className="flex flex-col items-center justify-center min-w-[64px] min-h-[56px] rounded-xl 
                                 bg-[var(--primary)] text-black active:scale-95 transition-transform"
                      data-testid="mobile-download-btn"
                    >
                      <Download className="w-6 h-6" />
                      <span className="text-xs font-medium mt-1">Save</span>
                    </button>

                    {/* Quick Print */}
                    <button
                      onClick={handlePrint}
                      className="flex flex-col items-center justify-center min-w-[64px] min-h-[56px] rounded-xl 
                                 bg-[var(--background-paper)] border border-[var(--border-primary)]
                                 text-[var(--text-primary)] active:scale-95 transition-transform"
                      data-testid="mobile-print-btn"
                    >
                      <Printer className="w-6 h-6" />
                      <span className="text-xs font-medium mt-1">Print</span>
                    </button>

                    {/* Share (if supported) */}
                    {navigator.share && (
                      <button
                        onClick={handleShare}
                        className="flex flex-col items-center justify-center min-w-[64px] min-h-[56px] rounded-xl 
                                   bg-[var(--background-paper)] border border-[var(--border-primary)]
                                   text-[var(--text-primary)] active:scale-95 transition-transform"
                        data-testid="mobile-share-btn"
                      >
                        <Share2 className="w-6 h-6" />
                        <span className="text-xs font-medium mt-1">Share</span>
                      </button>
                    )}
                  </div>

                  {/* More Actions Toggle */}
                  <button
                    onClick={() => setShowMobileActions(true)}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl 
                               text-[var(--text-secondary)] active:bg-white/5 transition-colors"
                  >
                    <span className="text-sm">More</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                /* Expanded Action Sheet */
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 py-4 space-y-3">
                    {/* Action Buttons - Large Touch Targets */}
                    <button
                      onClick={handleDownload}
                      className="w-full flex items-center gap-4 p-4 rounded-xl 
                                 bg-[var(--primary)] text-black active:scale-[0.98] transition-transform"
                      data-testid="mobile-download-expanded"
                    >
                      <div className="p-3 rounded-full bg-black/10">
                        <Download className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-base">Download PDF</p>
                        <p className="text-sm opacity-75">Save to your device</p>
                      </div>
                    </button>

                    <button
                      onClick={handlePrint}
                      className="w-full flex items-center gap-4 p-4 rounded-xl 
                                 bg-[var(--background-paper)] border border-[var(--border-primary)]
                                 text-[var(--text-primary)] active:scale-[0.98] transition-transform"
                      data-testid="mobile-print-expanded"
                    >
                      <div className="p-3 rounded-full bg-[var(--primary)]/10">
                        <Printer className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-base">Print Document</p>
                        <p className="text-sm text-[var(--text-secondary)]">Send to printer or save as PDF</p>
                      </div>
                    </button>

                    {navigator.share && (
                      <button
                        onClick={handleShare}
                        className="w-full flex items-center gap-4 p-4 rounded-xl 
                                   bg-[var(--background-paper)] border border-[var(--border-primary)]
                                   text-[var(--text-primary)] active:scale-[0.98] transition-transform"
                        data-testid="mobile-share-expanded"
                      >
                        <div className="p-3 rounded-full bg-blue-500/10">
                          <Share2 className="w-6 h-6 text-blue-500" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-base">Share</p>
                          <p className="text-sm text-[var(--text-secondary)]">Send via email, message, or AirDrop</p>
                        </div>
                      </button>
                    )}

                    {/* Collapse Button */}
                    <button
                      onClick={() => setShowMobileActions(false)}
                      className="w-full py-3 text-center text-[var(--text-secondary)] 
                                 active:bg-white/5 rounded-xl transition-colors"
                    >
                      Show Less
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PDFPreviewModal;
